import React from "react";
import {
  Search, ArrowRight, BookOpen, BookMarked, BarChart3, Video, Newspaper,
  FileText, Brain, Eye, Sofa, PaintRoller, ShoppingCart, Building2, Clock,
  Sparkles, Zap, Boxes, Plug, LayoutDashboard, ShieldCheck, GraduationCap,
  Code2, PlayCircle, HelpCircle, Award, Server, Workflow, Mail,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

import Navbar from "@/components/home/Navbar";
import Footer from "@/components/home/Footer";
import FinalCTA from "@/components/home/FinalCTA";
import HeroIntro from "@/components/home/HeroIntro";

import featuredImg from "@/assets/home-office.jpg";
import articleRetailImg from "@/assets/providedassets/1_The_Future_of_AI_in_Retail.png";
import articleConversionImg from "@/assets/providedassets/2_Why_Product_Visualization_Increases_Conversion_Rates.png";
import articleShowroomImg from "@/assets/providedassets/3_Virtual_Showrooms_vs_Traditional_Showrooms.png";
import articleReturnsImg from "@/assets/providedassets/4_How_AI_Reduces_Product_Returns.png";
import articleCxImg from "@/assets/providedassets/5_Building_Better_Customer_Experiences_with_AI.png";
import articlePlatformImg from "@/assets/providedassets/6_Choosing_the_Right_Product_Visualization_Platform.png";
import heroBg from "@/assets/elegant-armchair.jpg";

// All article/guide links are placeholders until real content exists.
const RESOURCE_TYPES = [
  { icon: BookOpen, title: "Blog", body: "Industry insights, thought leadership, and practical business advice." },
  { icon: BookMarked, title: "Guides", body: "Step-by-step implementation guides and best practices." },
  { icon: BarChart3, title: "Case Studies", body: "See how businesses are using NEIV to improve customer experiences." },
  { icon: Video, title: "Webinars", body: "Educational sessions with product experts and industry leaders." },
  { icon: Newspaper, title: "Product Updates", body: "Latest features, improvements, integrations, and platform announcements." },
  { icon: FileText, title: "Whitepapers", body: "Research reports and in-depth industry analysis." },
];

const CATEGORIES = [
  { icon: Brain, title: "Artificial Intelligence", body: "Explore how AI is transforming commerce, visualization, and customer engagement." },
  { icon: Eye, title: "Visual Commerce", body: "Learn how interactive product experiences improve buying decisions." },
  { icon: Sofa, title: "Furniture & Home Décor", body: "Industry trends, customer behavior, and retail innovation." },
  { icon: PaintRoller, title: "Paint, Tile & Interior Design", body: "Digital transformation for design-focused businesses." },
  { icon: ShoppingCart, title: "Retail & E-commerce", body: "Strategies for improving online and in-store shopping experiences." },
  { icon: Building2, title: "Enterprise Technology", body: "Scalable AI solutions for growing businesses." },
];

const ARTICLES = [
  { title: "The Future of AI in Retail", body: "Discover how AI is helping retailers create personalized shopping experiences and improve customer engagement.", time: "5 min read", img: articleRetailImg },
  { title: "Why Product Visualization Increases Conversion Rates", body: "Learn why customers buy more confidently when they can visualize products before purchase.", time: "7 min read", img: articleConversionImg },
  { title: "Virtual Showrooms vs Traditional Showrooms", body: "Compare digital and physical shopping experiences and understand where retail is heading.", time: "6 min read", img: articleShowroomImg },
  { title: "How AI Reduces Product Returns", body: "See how visualization helps customers make better purchasing decisions.", time: "6 min read", img: articleReturnsImg },
  { title: "Building Better Customer Experiences with AI", body: "Understand how AI-powered visualization creates more engaging buying journeys.", time: "8 min read", img: articleCxImg },
  { title: "Choosing the Right Product Visualization Platform", body: "A practical guide to evaluating AI visualization solutions for your business.", time: "10 min read", img: articlePlatformImg },
];

const GUIDES = [
  { title: "Getting Started with NEIV", body: "Learn how to create your first visualization in minutes." },
  { title: "Uploading Your Product Catalog", body: "Best practices for preparing products for AI visualization." },
  { title: "Creating QR-Based Shopping Experiences", body: "Enable customers to visualize products directly from your showroom." },
  { title: "Understanding Analytics", body: "Measure engagement, visualization activity, and customer behavior." },
  { title: "Integrating NEIV into Your Website", body: "Embed visualization experiences using APIs and widgets." },
];

const SUCCESS_STORIES = [
  "Furniture Retailer Increases Conversion Rates",
  "Paint Brand Improves Customer Confidence",
  "Interior Design Studio Accelerates Project Delivery",
  "Builder Enhances Property Sales",
];

const PRODUCT_UPDATES = [
  { icon: Sparkles, label: "New AI visualization models" },
  { icon: Zap, label: "Faster rendering performance" },
  { icon: Boxes, label: "Additional product category support" },
  { icon: Plug, label: "New integrations" },
  { icon: LayoutDashboard, label: "Dashboard enhancements" },
  { icon: ShieldCheck, label: "Security improvements" },
];

const LEARNING_CENTER = [
  { icon: GraduationCap, label: "Product Documentation" },
  { icon: Code2, label: "API Documentation" },
  { icon: PlayCircle, label: "Video Tutorials" },
  { icon: HelpCircle, label: "FAQs" },
  { icon: Award, label: "Best Practices" },
  { icon: Server, label: "Enterprise Deployment Guides" },
  { icon: Workflow, label: "Integration Guides" },
];

const FAQS = [
  {
    q: "Are all resources free?",
    a: "Yes. Most educational resources are freely available. Some advanced reports or webinars may require registration.",
  },
  {
    q: "How often are new articles published?",
    a: "We regularly publish articles, product updates, guides, and industry insights to keep our community informed.",
  },
  {
    q: "Can I contribute an article?",
    a: "We're always interested in collaborating with industry experts, customers, and technology partners. Contact us to discuss guest contributions.",
  },
  {
    q: "Where can I find technical documentation?",
    a: "Technical documentation, API references, and integration guides are available in the Learning Center.",
  },
];

export default function ResourcesPage() {
  return (
    <div className="min-h-screen bg-white text-neutral-900">
      <Navbar />
      <main>
        {/* Hero */}
        <section id="blogs" className="relative overflow-hidden bg-neutral-50 pb-16 pt-28 md:pb-20 md:pt-36">
          {/* Full-width background image */}
          <div className="pointer-events-none absolute inset-0 w-full" aria-hidden="true">
            <img
              src={heroBg}
              alt=""
              className="h-full w-full object-cover saturate-[1.15] contrast-[1.05]"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-neutral-50 via-neutral-50/45 to-neutral-50/15" />
            <div className="absolute inset-0 bg-gradient-to-t from-neutral-50/30 via-transparent to-transparent" />
          </div>

          <div className="relative mx-auto max-w-6xl px-4 md:px-8">
            <div className="max-w-xl">
              <HeroIntro eyebrow="Resources" heading="Knowledge, insights & resources">
                <p className="mt-5 text-lg leading-relaxed text-neutral-500">
                  Stay ahead with expert insights on AI, visual commerce, retail
                  innovation, interior design, product visualization, and the
                  future of customer experiences.
                </p>
                <form
                  className="mt-8 flex max-w-md items-center gap-2"
                  onSubmit={(e) => e.preventDefault()}
                >
                  <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-neutral-400" />
                    <input
                      type="search"
                      placeholder="Search resources…"
                      className="w-full rounded-lg border border-neutral-300 bg-white py-3.5 pl-12 pr-4 text-base text-neutral-800 outline-none transition-colors placeholder:text-neutral-400 focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <button
                    type="submit"
                    className="rounded-lg bg-primary px-6 py-3.5 text-base font-semibold text-white transition-colors hover:bg-primary/90"
                  >
                    Search
                  </button>
                </form>
              </HeroIntro>
            </div>
          </div>
        </section>

        {/* Featured resource */}
        <section className="bg-white py-24">
          <div className="mx-auto max-w-5xl px-4 md:px-8">
            <div className="overflow-hidden rounded-3xl border border-neutral-200 bg-white shadow-sm shadow-neutral-900/[0.03] lg:grid lg:grid-cols-5">
              <div className="relative min-h-[240px] lg:col-span-2 lg:block">
                <img
                  src={featuredImg}
                  alt="Featured article cover"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-white/40" />
              </div>
              <div className="p-10 md:p-14 lg:col-span-3">
                <p className="text-sm font-bold uppercase tracking-widest text-primary">Featured Resource</p>
                <h2 className="mt-4 max-w-2xl text-3xl font-bold text-neutral-900 md:text-4xl">
                  The Future of Visual Commerce: How AI is Transforming Retail
                </h2>
                <p className="mt-4 max-w-2xl text-lg leading-relaxed text-neutral-500">
                  Discover how AI-powered visualization is changing the way
                  customers evaluate products, helping businesses improve customer
                  confidence, increase conversions, and reduce returns.
                </p>
                <a
                  href="#"
                  className="group mt-8 inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-primary/90"
                >
                  Read Article
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Browse resources */}
        <section className="bg-neutral-50 py-24">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-sm font-bold uppercase tracking-widest text-primary">Browse Resources</p>
              <h2 className="mt-3 text-3xl font-bold text-neutral-900 md:text-4xl">
                Choose the content that matches your interests
              </h2>
            </div>
            <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {RESOURCE_TYPES.map((type) => {
                const Icon = type.icon;
                return (
                  <a
                    key={type.title}
                    href="#"
                    className="group rounded-2xl border border-neutral-200 bg-white p-8 shadow-sm shadow-neutral-900/[0.03] transition-shadow hover:shadow-md"
                  >
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="mt-5 text-xl font-bold text-neutral-900 group-hover:text-primary">
                      {type.title}
                    </h3>
                    <p className="mt-2 leading-relaxed text-neutral-500">{type.body}</p>
                  </a>
                );
              })}
            </div>
          </div>
        </section>

        {/* Featured categories */}
        <section className="bg-white py-24">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-sm font-bold uppercase tracking-widest text-primary">Featured Categories</p>
              <h2 className="mt-3 text-3xl font-bold text-neutral-900 md:text-4xl">
                Explore by topic
              </h2>
            </div>
            <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {CATEGORIES.map((category) => {
                const Icon = category.icon;
                return (
                  <div
                    key={category.title}
                    className="flex items-start gap-4 rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm shadow-neutral-900/[0.03]"
                  >
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-bold text-neutral-900">{category.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-neutral-500">{category.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Latest articles */}
        <section className="bg-neutral-50 py-24">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-sm font-bold uppercase tracking-widest text-primary">Latest Articles</p>
              <h2 className="mt-3 text-3xl font-bold text-neutral-900 md:text-4xl">
                Fresh insights from our team
              </h2>
            </div>
            <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {ARTICLES.map((article) => (
                <a
                  key={article.title}
                  href="#"
                  className="group flex flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm shadow-neutral-900/[0.03] transition-shadow hover:shadow-md"
                >
                  <div className="h-44 overflow-hidden">
                    <img
                      src={article.img}
                      alt={article.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-8">
                    <h3 className="text-lg font-bold text-neutral-900 group-hover:text-primary">
                      {article.title}
                    </h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-neutral-500">{article.body}</p>
                    <p className="mt-5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-neutral-400">
                      <Clock className="h-3.5 w-3.5" />
                      {article.time}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Popular guides */}
        <section className="bg-white py-24">
          <div className="mx-auto max-w-4xl px-4 md:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-sm font-bold uppercase tracking-widest text-primary">Popular Guides</p>
              <h2 className="mt-3 text-3xl font-bold text-neutral-900 md:text-4xl">
                Get up and running fast
              </h2>
            </div>
            <div className="mt-12 divide-y divide-neutral-100 overflow-hidden rounded-2xl border border-neutral-200 shadow-sm shadow-neutral-900/[0.03]">
              {GUIDES.map((guide) => (
                <a
                  key={guide.title}
                  href="#"
                  className="group flex items-center justify-between gap-4 bg-white px-8 py-6 transition-colors hover:bg-neutral-50"
                >
                  <div>
                    <h3 className="font-bold text-neutral-900 group-hover:text-primary">{guide.title}</h3>
                    <p className="mt-1 text-sm text-neutral-500">{guide.body}</p>
                  </div>
                  <ArrowRight className="h-5 w-5 shrink-0 text-neutral-300 transition-transform group-hover:translate-x-1 group-hover:text-primary" />
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Success stories */}
        <section className="bg-neutral-50 py-24">
          <div className="mx-auto max-w-5xl px-4 md:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-sm font-bold uppercase tracking-widest text-primary">Customer Success Stories</p>
              <h2 className="mt-3 text-3xl font-bold text-neutral-900 md:text-4xl">
                Businesses transforming customer experiences
              </h2>
              <p className="mt-4 text-neutral-400">
                Real customer stories will be featured here as they become available.
              </p>
            </div>
            <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2">
              {SUCCESS_STORIES.map((story) => (
                <div
                  key={story}
                  className="flex items-center gap-4 rounded-2xl border border-dashed border-neutral-300 bg-white p-6"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <BarChart3 className="h-5 w-5" />
                  </div>
                  <p className="font-semibold text-neutral-700">{story}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Product updates + learning center */}
        <section className="bg-white py-24">
          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-4 md:grid-cols-2 md:px-8">
            <div className="rounded-2xl border border-neutral-200 bg-white p-10 shadow-sm shadow-neutral-900/[0.03]">
              <p className="text-sm font-bold uppercase tracking-widest text-primary">Product Updates</p>
              <h3 className="mt-3 text-2xl font-bold text-neutral-900">
                Stay informed about new capabilities
              </h3>
              <ul className="mt-6 space-y-4">
                {PRODUCT_UPDATES.map((update) => {
                  const Icon = update.icon;
                  return (
                    <li key={update.label} className="flex items-center gap-3">
                      <Icon className="h-5 w-5 shrink-0 text-primary" />
                      <span className="font-medium text-neutral-700">{update.label}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="rounded-2xl border border-neutral-200 bg-white p-10 shadow-sm shadow-neutral-900/[0.03]">
              <p className="text-sm font-bold uppercase tracking-widest text-primary">Learning Center</p>
              <h3 className="mt-3 text-2xl font-bold text-neutral-900">
                Expand your knowledge
              </h3>
              <ul className="mt-6 space-y-4">
                {LEARNING_CENTER.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.label}>
                      <a href="#" className="group flex items-center gap-3">
                        <Icon className="h-5 w-5 shrink-0 text-primary" />
                        <span className="font-medium text-neutral-700 group-hover:text-primary">
                          {item.label}
                        </span>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </section>

        {/* Subscribe */}
        <section className="bg-neutral-50 py-24">
          <div className="mx-auto max-w-3xl px-4 text-center md:px-8">
            <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Mail className="h-6 w-6" />
            </div>
            <h2 className="mt-5 text-3xl font-bold text-neutral-900 md:text-4xl">
              Never miss an update
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-lg text-neutral-500">
              Receive the latest articles, product announcements, industry
              insights, and educational resources directly in your inbox.
            </p>
            <form
              className="mx-auto mt-8 flex max-w-md flex-col gap-3 sm:flex-row"
              onSubmit={(e) => e.preventDefault()}
            >
              <input
                type="email"
                required
                placeholder="Email address"
                className="flex-1 rounded-lg border border-neutral-300 bg-white px-4 py-3.5 text-base text-neutral-800 outline-none transition-colors placeholder:text-neutral-400 focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
              <button
                type="submit"
                className="rounded-lg bg-primary px-6 py-3.5 text-base font-semibold text-white transition-colors hover:bg-primary/90"
              >
                Subscribe
              </button>
            </form>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-white py-24">
          <div className="mx-auto max-w-3xl px-4 md:px-8">
            <div className="text-center">
              <p className="text-sm font-bold uppercase tracking-widest text-primary">FAQ</p>
              <h2 className="mt-3 text-3xl font-bold text-neutral-900 md:text-4xl">
                Frequently asked questions
              </h2>
            </div>
            <Accordion type="single" collapsible className="mt-12">
              {FAQS.map((faq, i) => (
                <AccordionItem
                  key={faq.q}
                  value={`faq-${i}`}
                  className="mb-3 rounded-xl border border-neutral-200 bg-white px-6 shadow-sm shadow-neutral-900/[0.02]"
                >
                  <AccordionTrigger className="text-left text-base font-semibold text-neutral-900 hover:no-underline">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-base leading-relaxed text-neutral-500">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        <FinalCTA
          title="Ready to explore visual commerce?"
          subtitle="Whether you're just beginning your AI journey or looking to scale visualization across your business, our resources are here to help."
          primaryLabel="Start Free"
          secondaryLabel="Book a Demo"
        />
      </main>
      <Footer />
    </div>
  );
}
